import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, forkJoin, from, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, withLatestFrom } from 'rxjs/operators';
import {
	AVAILABILITY_FAILED,
	AVAILABILITY_FIND_ALL,
	AVAILABILITY_FIND_ALL_SUCCESS,
	CART_CLEAR,
	CHECKOUT_PLACE_ORDER,
	CHECKOUT_CREATE_CASH_ORDER,
	CHECKOUT_CREATE_CARD_ORDER,
	CHECKOUT_PLACE_ORDER_FAILURE,
	CHECKOUT_PLACE_ORDER_SUCCESS,
	CHECKOUT_UPDATE_LOADING,
	CHECKOUT_VALIDATE_AVAILABILITY_PRODUCT_ORDER_FAILURE,
	CHECKOUT_VALIDATE_AVAILABILITY_PRODUCT_ORDER_SUCCESS,
	CHECKOUT_VALIDATE_ORDER,
	CLEAR_VAULTED_CARDS_STATE,
	NOTIFICATION_SHOW,
	PRODUCT_ALL_UPDATE_STOCK,
	CART_FIND_SUCCESS,
	COUPON_APPLY,
	CHECKOUT_PLACE_ORDER_SEND_EMAIL
} from '@store/actions';
import { CHECK_BUSINESS_DAY, CHECK_BUSINESS_DAY_FAILURE, CHECK_BUSINESS_DAY_SUCCESS } from '../actions/store.actions';
import { PROCESS_PAYMENT, PROCESS_PAYMENT_FAILURE, PROCESS_PAYMENT_SUCCESS } from '../actions/pay.actions';
import { CheckoutService } from '../../core/services/checkout.service';
import {
	selectAvailabilityList,
	selectDefaultVaultedCard,
	selectIsLoggedIn,
	selectLocalChannelId,
	selectLocalChannelSesion,
	selectMenu,
	selectOrdenForm,
	selectSelectedVaultedCard,
	selectTokenFromAssociatedCard,
} from '@store/selectors';
import { MerchantMXHelper } from '@core/helpers/merchantMXHelper';
import { CartService, IntegerPrecisionService, MenuItemService, WorkDayService } from '@core/services';
import { OrderForm, PaymentResult } from '@core/models';
import {
	getCheckoutConfirmationEmailData,
} from '@/features/checkout/domain/util';
import { CheckoutOrderProcessor, CheckoutPriceLookupResult } from '@/features/checkout/domain/processor';
import { CheckoutPaymentPlanType } from '@/features/checkout/domain/enum/checkout-flow.enum';
import { CheckoutValidationException } from '@/features/checkout/domain/checkout-validation.exception';
import { CartProcessorService } from '@/features/shoppingcart/domain';
import { cartStateProcessor } from '@core/domain/cart';
import { CheckoutAsyncResult } from '@/features/checkout/domain/interfaces/checkout-validation-flow.interface';

@Injectable()
export class CheckoutEffects {
	constructor(
		private store: Store,
		private actions$: Actions,
		private checkoutService: CheckoutService,
		private cartService: CartService,
		private merchantMXHelper: MerchantMXHelper,
		private workdayService: WorkDayService,
		private menuItemService: MenuItemService,
		private integerPrecisionService: IntegerPrecisionService,
		private checkoutOrderProcessor: CheckoutOrderProcessor,
		private cartProcessor: CartProcessorService,
	) { }

	validateOrderAvailabilityProducts$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CHECKOUT_VALIDATE_ORDER),
			withLatestFrom(
				this.cartService.getStoreForStore(),
				this.store.select(selectMenu),
				this.store.select(selectLocalChannelId),
				this.store.select(selectLocalChannelSesion),
				this.store.select(selectOrdenForm),
				this.store.select(selectSelectedVaultedCard),
				this.store.select(selectDefaultVaultedCard),
				this.store.select(selectTokenFromAssociatedCard),
				this.store.select(selectIsLoggedIn),
				this.store.select(selectAvailabilityList)
			),
			switchMap(([
				_,
				cartList,
				menu,
				localChannelId,
				localChannelSession,
				orderForm,
				selectedCard,
				defaultCard,
				associatedCardToken,
				isLoggedIn,
				availabilityList,
			]) => {
				const cartItems = cartList ?? [];
				this.store.dispatch(CHECKOUT_UPDATE_LOADING({ loading: true }));
				this.store.dispatch(AVAILABILITY_FIND_ALL());
				this.store.dispatch(CHECK_BUSINESS_DAY());

				const checkAvailability$ = this.actions$.pipe(
					ofType(AVAILABILITY_FIND_ALL_SUCCESS, AVAILABILITY_FAILED),
					take(1)
				);
				const checkBusinessDay$ = this.actions$.pipe(
					ofType(CHECK_BUSINESS_DAY_SUCCESS, CHECK_BUSINESS_DAY_FAILURE),
					take(1)
				);
				const checkStoreAvailability$ = this.workdayService
					.validateStoreOpen(orderForm.localChannelId)
					.pipe(
						take(1),
						map(serviceAvailability => ({
							succeeded: true as const,
							value: serviceAvailability,
						})),
						catchError(error =>
							of({
								succeeded: false as const,
								error: error?.message || 'Unable to verify service availability',
							})
						)
					);

				const menuItemsCheck$ = this.menuItemService
					.getMenuItemList(
						cartItems.map(item => item.idArticulo),
						null,
						null,
						localChannelId,
						menu?.idMenu
					)
					.pipe(
						map(response => {
							const updatedCartItems = this.cartProcessor.syncMenuItemStock(
								cartItems,
								response.data
							);
							this.store.dispatch(CART_FIND_SUCCESS({ items: updatedCartItems }));
							return {
								succeeded: true as const,
								value: {
									cartItems: updatedCartItems,
									updatedMenuItems: response.data,
								},
							};
						}),
						catchError(error =>
							of({
								succeeded: false as const,
								error: error?.message || 'Unable to refresh product stock',
							})
						)
					);

				return forkJoin({
					availabilityAction: checkAvailability$,
					businessDayAction: checkBusinessDay$,
					storeAvailability: checkStoreAvailability$,
					menuItemsCheck: menuItemsCheck$,
				}).pipe(
					switchMap(({
						availabilityAction,
						businessDayAction,
						storeAvailability,
						menuItemsCheck,
					}) => {
						const checkedAvailabilityList =
							availabilityAction.type === AVAILABILITY_FIND_ALL_SUCCESS.type
								? availabilityAction.availabilityList
								: availabilityList;
						const stockUpdate = menuItemsCheck.succeeded
							? { succeeded: true as const, value: menuItemsCheck.value.cartItems }
							: menuItemsCheck;
						let priceCheck: CheckoutAsyncResult<CheckoutPriceLookupResult>;
						if (menuItemsCheck.succeeded) {
							priceCheck = {
								succeeded: true,
								value: this.checkoutOrderProcessor.processCheckoutPriceLookup(
									menuItemsCheck.value.updatedMenuItems,
									cartItems,
									checkedAvailabilityList,
									this.integerPrecisionService,
									this.cartProcessor
								),
							};
						} else {
							priceCheck = { succeeded: false, error: menuItemsCheck.error };
						}
						const checkedCartItems = menuItemsCheck.succeeded
							? menuItemsCheck.value.cartItems
							: cartItems;
						const result = this.checkoutOrderProcessor.processCheckoutValidation({
							orderForm,
							cartItems,
							asyncResults: {
								availability: availabilityAction,
								priceLookup: priceCheck,
								businessDay: businessDayAction,
								serviceAvailability: storeAvailability,
								stockUpdate,
							},
							unavailableProducts: cartStateProcessor.existProductOutStock(
								checkedCartItems,
								checkedAvailabilityList,
								localChannelSession
							),
							selectedCardToken: selectedCard?.vaultedCardToken,
							defaultCardToken: defaultCard?.vaultedCardToken,
							associatedCardToken,
							isLoggedIn,
							paymentAmount: this.integerPrecisionService.toDecimalNumberFromVisual(
								orderForm.cartTotals.total
							),
						});

						if (result.failure) {
							const actions: Action[] = [
								CHECKOUT_UPDATE_LOADING({ loading: false }),
								CHECKOUT_VALIDATE_AVAILABILITY_PRODUCT_ORDER_FAILURE(),
								CHECKOUT_PLACE_ORDER_FAILURE({ error: result.failure }),
							];

							if (!!result.failure.updatedCartItems?.length)
								actions.push(CART_FIND_SUCCESS({ items: result.failure.updatedCartItems }))

							return from(actions);
						} else {
							return from([
								CHECKOUT_VALIDATE_AVAILABILITY_PRODUCT_ORDER_SUCCESS(),
								CHECKOUT_PLACE_ORDER({ paymentPlan: result.paymentPlan })
							]);
						}
					})
				);
			})
		)
	);

	validationFailureNotification$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CHECKOUT_PLACE_ORDER_FAILURE),
			filter(
				(action): action is typeof action & { error: CheckoutValidationException } =>
					action.error instanceof CheckoutValidationException
			),
			mergeMap(({ error }) => {
				return from([
					NOTIFICATION_SHOW({
						notification: {
							type: error.notificationType,
							title: error.title,
							detail: error.message,
						},
					}),
				]);
			})
		)
	);

	create$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CHECKOUT_PLACE_ORDER),
			map(({ paymentPlan }) =>
				paymentPlan.type === CheckoutPaymentPlanType.Card
					? CHECKOUT_CREATE_CARD_ORDER({ paymentPlan })
					: CHECKOUT_CREATE_CASH_ORDER({ order: paymentPlan.order })
			)
		)
	);

	createCashOrder$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CHECKOUT_CREATE_CASH_ORDER),
			switchMap(({ order }) =>
				this.checkoutService.create(order).pipe(
					map(response => CHECKOUT_PLACE_ORDER_SUCCESS({ order: response, orderForm: order })),
					catchError(error => of(CHECKOUT_PLACE_ORDER_FAILURE({ error })))
				)
			)
		)
	);

	createCardOrder$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CHECKOUT_CREATE_CARD_ORDER),
			switchMap(({ paymentPlan }) => {
				this.store.dispatch(PROCESS_PAYMENT(paymentPlan.request));
				return this.actions$.pipe(
					ofType(PROCESS_PAYMENT_SUCCESS, PROCESS_PAYMENT_FAILURE),
					take(1),
					switchMap(paymentAction => {
						if (paymentAction.type === PROCESS_PAYMENT_FAILURE.type) {
							return of(CHECKOUT_PLACE_ORDER_FAILURE({ error: paymentAction.error }));
						}

						const paymentResponse = paymentAction.response;
						const paymentResult: PaymentResult =
							this.merchantMXHelper.evaluatePaymentResponse(paymentResponse);
						if (
							!this.checkoutOrderProcessor.acceptsCheckoutPaymentResult(
								paymentPlan.order.paymentMethod,
								paymentResult
							)
						) {
							return of(CHECKOUT_PLACE_ORDER_FAILURE({ error: paymentResult }));
						}

						const order: OrderForm = {
							...paymentPlan.order,
							paymentCard: {
								...paymentResult,
								idPayment: paymentResponse?.data?.idPayment,
							},
						};
						return this.checkoutService.create(order).pipe(
							map(response => CHECKOUT_PLACE_ORDER_SUCCESS({ order: response, orderForm: order })),
							catchError(error => of(CHECKOUT_PLACE_ORDER_FAILURE({ error })))
						);
					})
				);
			})
		)
	);

	clearStateAfterOrderSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CHECKOUT_PLACE_ORDER_SUCCESS),
			mergeMap(({ order, orderForm }) =>
				from([
					COUPON_APPLY(),
					PRODUCT_ALL_UPDATE_STOCK(),
					CART_CLEAR(),
					CLEAR_VAULTED_CARDS_STATE(),
					CHECKOUT_PLACE_ORDER_SEND_EMAIL({ order, orderForm })
				])
			)
		)
	);

	sendConfirmationEmail$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(CHECKOUT_PLACE_ORDER_SEND_EMAIL),
				map(({ order, orderForm }) => {
					return getCheckoutConfirmationEmailData(order, orderForm);
				}),
				filter(({ idAccount, email }) => !!idAccount && !!email),
				switchMap(({ idAccount, email }) =>
					this.checkoutService.sendEmail(idAccount!, email!).pipe(
						catchError(error => {
							console.error('Error enviando correo de confirmación:', error);
							return EMPTY;
						})
					)
				)
			),
		{ dispatch: false }
	);
}
